import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { UserInfo, CreateUserDto, UpdateUserDto, DistrictDto, BlockDto } from "@/lib/types/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Plus, Pencil, Trash2, X, Users as UsersIcon, Search, Filter } from "lucide-react";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/utils/errors";
import { useAuth } from "@/lib/contexts/auth-context";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";

const userSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(50, "Username cannot exceed 50 characters")
    .regex(/^[a-zA-Z0-9_-]+$/, "Username can only contain letters, numbers, hyphens and underscores"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  role: z.enum(["SuperAdmin", "Admin", "User"]),
  districtId: z.string().optional(),
  blockId: z.string().optional(),
});

type UserFormData = z.infer<typeof userSchema>;

const ROLES = [
  { value: "SuperAdmin", label: "Super Admin" },
  { value: "Admin", label: "Admin" },
  { value: "User", label: "User" },
];

export default function UsersPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole("SuperAdmin");
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [districts, setDistricts] = useState<DistrictDto[]>([]);
  const [blocks, setBlocks] = useState<BlockDto[]>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserInfo | null>(null);

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Sorting states
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
  });

  const fetchUsers = useCallback(async (overrides?: { search?: string; role?: string; district?: string; page?: number }) => {
    try {
      setIsLoading(true);
      const usersResponse = await apiClient.getUsers({
        page: overrides?.page ?? currentPage,
        pageSize: pageSize,
        search: (overrides?.search ?? searchTerm) || undefined,
        role: (overrides?.role ?? roleFilter) || undefined,
        districtId: (overrides?.district ?? districtFilter) || undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      
      if (usersResponse.success && usersResponse.data) {
        const items = usersResponse.data.items || usersResponse.data;
        setUsers(Array.isArray(items) ? items : []);
        const totalCount = usersResponse.data.totalCount || (Array.isArray(items) ? items.length : 0);
        setTotalUsers(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to load users"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortBy, sortDescending, searchTerm, roleFilter, districtFilter]);

  // Load districts once on mount
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const response = await apiClient.getDistricts();
        if (response.success && response.data) {
          setDistricts(response.data);
        }
      } catch (error) {
        toast.error(handleApiError(error, "Failed to load districts"));
      }
    };
    loadDistricts();
  }, []);

  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes, load immediately without debounce
    if (searchTerm === "" && roleFilter === "" && districtFilter === "") {
      fetchUsers();
      return;
    }

    // For search/filter changes, debounce
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchUsers({ search: searchTerm, role: roleFilter, district: districtFilter });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm, roleFilter, districtFilter, currentPage, pageSize, fetchUsers]);

  const loadBlocksByDistrict = async (districtId: string) => {
    if (!districtId) {
      setBlocks([]);
      return;
    }
    try {
      const response = await apiClient.getBlocksByDistrict(districtId);
      if (response.success && response.data) {
        setBlocks(response.data);
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to load blocks"));
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setSelectedDistrictId("");
    setBlocks([]);
    reset({
      username: "",
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      role: "User",
      districtId: undefined,
      blockId: undefined,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user: UserInfo) => {
    setEditingUser(user);
    const districtId = user.districtId || "";
    setSelectedDistrictId(districtId);
    if (districtId) {
      loadBlocksByDistrict(districtId);
    }
    reset({
      username: user.username,
      email: user.email,
      password: "",
      firstName: user.firstName,
      lastName: user.lastName,
      role: (user.roles[0] || "User") as "SuperAdmin" | "Admin" | "User",
      districtId: districtId,
      blockId: user.blockId || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setSelectedDistrictId("");
    setBlocks([]);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        // Update existing user
        const updateData: UpdateUserDto = {
          username: data.username,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          districtId: data.districtId || null,
          blockId: data.blockId || null,
        };
        if (data.password) {
          updateData.password = data.password;
        }
        await apiClient.updateUser(editingUser.id, updateData);
        toast.success("User updated successfully");
        closeModal();
        await fetchUsers();
      } else {
        // Create new user
        const createData: CreateUserDto = {
          username: data.username,
          email: data.email,
          password: data.password || "",
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          districtId: data.districtId || null,
          blockId: data.blockId || null,
        };
        await apiClient.createUser(createData);
        toast.success("User created successfully");
        closeModal();
        await fetchUsers();
      }
    } catch (error) {
      toast.error(handleApiError(error, editingUser ? "Failed to update user" : "Failed to create user"));
    }
  };

  const handleDelete = async (user: UserInfo) => {
    try {
      const response = await apiClient.deleteUser(user.id);
      if (response.success) {
        toast.success("User deleted successfully");
        await fetchUsers();
        setDeleteConfirmUser(null);
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to delete user"));
    }
  };

  const handleFilterChange = (type: 'search' | 'role' | 'district', value: string) => {
    if (type === 'search') {
      setSearchTerm(value);
    } else if (type === 'role') {
      setRoleFilter(value);
    } else if (type === 'district') {
      setDistrictFilter(value);
    }
    // Page reset happens in debounce effect
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
    setDistrictFilter("");
    setCurrentPage(1);
    // Immediately fetch with cleared filters
    fetchUsers({ search: "", role: "", district: "", page: 1 });
  };

  return (
    <>
      <div className="space-y-6">
        {/* Users Table */}
        <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <UsersIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">User Management</CardTitle>
                <CardDescription className="mt-1">Manage system users and roles</CardDescription>
              </div>
            </div>
            <Button onClick={openCreateModal} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filter Controls */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search & Filters</h3>
              {totalUsers > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalUsers}</span>
                  <span>Total Users</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, username, or email..."
                  value={searchTerm}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9 h-10"
                />
              </div>
              
              <Select value={roleFilter || "all"} onValueChange={(value) => handleFilterChange('role', value === "all" ? "" : value)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={districtFilter || "all"} onValueChange={(value) => handleFilterChange('district', value === "all" ? "" : value)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Districts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Districts</SelectItem>
                  {districts.map((district) => (
                    <SelectItem key={district.id} value={district.id}>
                      {district.districtName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(searchTerm || roleFilter || districtFilter) && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                      Search: {searchTerm}
                      <button onClick={() => setSearchTerm("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                    </span>
                  )}
                  {roleFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md text-xs">
                      Role: {ROLES.find(r => r.value === roleFilter)?.label}
                      <button onClick={() => setRoleFilter("")} className="hover:text-purple-900 dark:hover:text-purple-100">×</button>
                    </span>
                  )}
                  {districtFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md text-xs">
                      District: {districts.find(d => d.id === districtFilter)?.districtName}
                      <button onClick={() => setDistrictFilter("")} className="hover:text-green-900 dark:hover:text-green-100">×</button>
                    </span>
                  )}
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
          {users.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                <UsersIcon className="h-8 w-8 text-gray-400 dark:text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {searchTerm || roleFilter || districtFilter
                  ? "No users match your filters" 
                  : "No users found"
                }
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                {searchTerm || roleFilter || districtFilter
                  ? "Try adjusting your search terms or filters to find what you're looking for"
                  : "Get started by adding your first user"
                }
              </p>
              {(searchTerm || roleFilter || districtFilter) ? (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear All Filters
                </Button>
              ) : (
                <Button onClick={openCreateModal}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First User
                </Button>
              )}
            </div>
          ) : (
            (() => {
              const columns: Column<UserInfo>[] = [
                {
                  key: "name",
                  header: "Name",
                  sortable: true,
                  render: (user) => (
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {user.firstName} {user.lastName}
                    </span>
                  ),
                },
                {
                  key: "username",
                  header: "Username",
                  sortable: true,
                  render: (user) => (
                    <span className="text-sm text-gray-600 dark:text-gray-400">{user.username}</span>
                  ),
                },
                {
                  key: "email",
                  header: "Email",
                  sortable: true,
                  render: (user) => (
                    <span className="text-sm text-gray-600 dark:text-gray-400">{user.email}</span>
                  ),
                },
                {
                  key: "role",
                  header: "Role",
                  sortable: true,
                  render: (user) => (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.roles.includes("SuperAdmin")
                        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                        : user.roles.includes("Admin")
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                        : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                    }`}>
                      {user.roles[0] || "User"}
                    </span>
                  ),
                },
                {
                  key: "assignment",
                  header: "Assignment",
                  render: (user) => user.districtName ? (
                    <div className="text-sm space-y-0.5">
                      <div className="text-gray-700 dark:text-gray-300">{user.districtName}</div>
                      {user.blockName && (
                        <div className="text-gray-500 dark:text-gray-500 text-xs">→ {user.blockName}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">No assignment</span>
                  ),
                },
              ];

              return (
                <DataTable
                  data={users}
                  columns={columns}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={totalUsers}
                  totalPages={totalPages}
                  loading={isLoading}
                  sortBy={sortBy}
                  sortDescending={sortDescending}
                  onSort={handleSort}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(size) => setPageSize(size)}
                  onRefresh={fetchUsers}
                  getRowKey={(user) => user.id}
                  actionsRenderer={(user) => (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditModal(user)}
                        disabled={user.roles.includes("SuperAdmin")}
                        className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                        title={user.roles.includes("SuperAdmin") ? "SuperAdmin users cannot be edited" : "Edit user"}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmUser(user)}
                        disabled={user.roles.includes("SuperAdmin")}
                        className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                        title={user.roles.includes("SuperAdmin") ? "SuperAdmin users cannot be deleted" : "Delete user"}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                  emptyMessage={
                    searchTerm || roleFilter || districtFilter
                      ? "No users match your filters"
                      : "No users found"
                  }
                />
              );
            })()
          )}
        </CardContent>
      </Card>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9998]">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <CardHeader className="border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl">{editingUser ? "Edit User" : "Create New User"}</CardTitle>
                  <CardDescription className="mt-1">
                    {editingUser ? "Update user information and permissions" : "Add a new user to the system"}
                  </CardDescription>
                </div>
                <button 
                  onClick={closeModal} 
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Row 1: Name and Email */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="First Name"
                    type="text"
                    placeholder="John"
                    error={errors.firstName?.message}
                    {...register("firstName")}
                  />
                  <Input
                    label="Last Name"
                    type="text"
                    placeholder="Doe"
                    error={errors.lastName?.message}
                    {...register("lastName")}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="john@example.com"
                    error={errors.email?.message}
                    {...register("email")}
                  />
                </div>

                {/* Row 2: Username and Password */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Username"
                    type="text"
                    placeholder="johndoe"
                    error={errors.username?.message}
                    {...register("username")}
                  />
                  <Input
                    label={editingUser ? "Password (leave blank to keep current)" : "Password"}
                    type="password"
                    placeholder="••••••••"
                    error={errors.password?.message}
                    {...register("password")}
                  />
                </div>

                {/* Row 3: Role, District, and Block */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Role
                      {!isSuperAdmin && <span className="text-xs text-gray-500 ml-1">(SuperAdmin only)</span>}
                    </label>
                    <select
                      {...register("role")}
                      disabled={!isSuperAdmin}
                      className={`w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                        !isSuperAdmin ? "bg-gray-100 dark:bg-gray-800/50 cursor-not-allowed border-gray-300 dark:border-gray-700" : ""
                      }`}
                    >
                      {ROLES.filter(role => role.value !== "SuperAdmin").map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    {errors.role && (
                      <p className="mt-2 text-sm text-red-400">{errors.role.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      District (Optional)
                    </label>
                    <select
                      {...register("districtId")}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedDistrictId(value);
                        setValue("districtId", value || undefined);
                        setValue("blockId", undefined);
                        if (value) {
                          loadBlocksByDistrict(value);
                        } else {
                          setBlocks([]);
                        }
                      }}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">Select District</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.districtName}
                        </option>
                      ))}
                    </select>
                    {errors.districtId && (
                      <p className="mt-2 text-sm text-red-400">{errors.districtId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Block (Optional)
                    </label>
                    <select
                      {...register("blockId")}
                      disabled={!selectedDistrictId || blocks.length === 0}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed disabled:border-gray-300 dark:disabled:border-gray-700 transition-all"
                    >
                      <option value="">Select Block</option>
                      {blocks.map((block) => (
                        <option key={block.id} value={block.id}>
                          {block.blockName}
                        </option>
                      ))}
                    </select>
                    {errors.blockId && (
                      <p className="mt-2 text-sm text-red-400">{errors.blockId.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : editingUser ? "Update User" : "Create User"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9998]">
          <Card className="w-full max-w-md shadow-2xl m-4">
            <CardHeader className="border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <Trash2 className="h-6 w-6 text-red-500" />
                  </div>
                  <div>
                    <CardTitle>Confirm Deletion</CardTitle>
                    <CardDescription className="mt-1">
                      This action cannot be undone
                    </CardDescription>
                  </div>
                </div>
                <button 
                  onClick={() => setDeleteConfirmUser(null)} 
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  Are you sure you want to delete this user?
                </p>
                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">
                    {deleteConfirmUser.firstName} {deleteConfirmUser.lastName}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{deleteConfirmUser.email}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirmUser(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(deleteConfirmUser)}
                >
                  Delete User
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
